import { QuestInterface } from "@swivl/swivl-lib";

import styled from "@emotion/styled";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import type { DraggableProvided, DraggableStateSnapshot, DroppableProvided, DroppableStateSnapshot } from "@hello-pangea/dnd";
import React, { CSSProperties, ReactElement } from "react";

import BoardQuestItem from "./Item/BoardQuestItem";

export const getBackgroundColor = (isDraggingOver: boolean, isDraggingFrom: boolean, isDraggingAQuest: boolean): string => {
  if (isDraggingOver) {
    return "#FFF5E2";
  }
  if (isDraggingAQuest) {
    return "#FDF6F3";
  }

  if (isDraggingFrom) {
    return "#DAD3D0";
  }
  return "transparent";
};

interface WrapperProps {
  isDraggingOver: boolean;
  isDraggingFrom: boolean;
  isDropDisabled: boolean;
  isDraggingAQuest: boolean;
  hasQuests: boolean;
}

const Wrapper = styled.div<WrapperProps>`
  background-color: ${(props) => getBackgroundColor(props.isDraggingOver, props.isDraggingFrom, props.isDraggingAQuest)};
  display: flex;
  flex-direction: column;
  opacity: ${({ isDropDisabled }) => (isDropDisabled ? 0.5 : "inherit")};
  box-shadow: ${({ isDraggingOver }) => (isDraggingOver ? `0 0px 4px rgba(0,0,0,0.25)` : "none")};

  ${({ hasQuests, isDraggingAQuest }) =>
    isDraggingAQuest || hasQuests
      ? `
    padding: 4px;
    margin: 4px 4px 4px 4px;
    `
      : `  padding: 0;
    margin: 0 0
    background-color: red;
    height: 0;
    `}

  border-radius: 8px;
  padding-bottom: 0;
  transition:
    background-color 0.2s ease,
    opacity 0.1s ease;
  user-select: none;
  width: 250px;
`;

const scrollContainerHeight = 250;

interface DropZoneProps {
  isDragingAQuestAndIsEmpty;
}
const DropZone = styled.div<DropZoneProps>`
  /* stop the list collapsing when empty */
  min-height: ${({ isDragingAQuestAndIsEmpty }) => (isDragingAQuestAndIsEmpty ? `49px` : "0")};

  /*
    not relying on the items for a margin-bottom
    as it will collapse when the list is empty
  */
  padding-bottom: 4px;
`;

/* stylelint-disable block-no-empty */
const Container = styled.div``;

/* stylelint-enable */

interface Props {
  listId?: string;
  listType?: string;
  quests: QuestInterface[];
  title?: string;
  //   internalScroll?: boolean;
  //   scrollContainerStyle?: CSSProperties;
  isDropDisabled?: boolean;
  isCombineEnabled?: boolean;
  style?: CSSProperties;
  // may not be provided - and might be null
  ignoreContainerClipping?: boolean;
  //   useClone?: boolean;
  isDraggingAQuest?: boolean;
  canEdit: boolean;
}

interface InnerBoardQuestListProps {
  quests: QuestInterface[];
  canEdit: boolean;
}

function InnerBoardQuestList(props: InnerBoardQuestListProps): ReactElement {
  return (
    <>
      {props.quests.map((quest: QuestInterface, index: number) => (
        <Draggable key={quest.slug} draggableId={quest.slug} index={index} isDragDisabled={!props.canEdit}>
          {(dragProvided: DraggableProvided, dragSnapshot: DraggableStateSnapshot) => (
            <BoardQuestItem
              key={quest.slug}
              quest={quest}
              isDragging={dragSnapshot.isDragging}
              // isGroupedOver={Boolean(dragSnapshot.combineTargetFor)}
              provided={dragProvided}
            />
          )}
        </Draggable>
      ))}
    </>
  );
}

const InnerBoardQuestListMemo = React.memo<InnerBoardQuestListProps>(InnerBoardQuestList);

interface InnerListProps {
  isDraggingAQuest: boolean;

  dropProvided: DroppableProvided;
  quests: QuestInterface[];
  draggingOverWith: string | null | undefined;
  title: string | undefined | null;
  canEdit: boolean;
}

function InnerList(props: InnerListProps) {
  const { isDraggingAQuest, quests, dropProvided, draggingOverWith, canEdit } = props;
  const title = props.title ? <h2>{props.title}</h2> : null;
  if (draggingOverWith) {
  }
  return (
    <Container>
      <DropZone ref={dropProvided.innerRef} isDragingAQuestAndIsEmpty={Boolean(isDraggingAQuest && quests.length === 0)}>
        <InnerBoardQuestListMemo quests={quests} canEdit={canEdit} />
        {dropProvided.placeholder}
      </DropZone>
    </Container>
  );
}

export default function BoardQuestList(props: Props): ReactElement {
  const {
    ignoreContainerClipping,

    isDropDisabled,
    isCombineEnabled,
    listId = "LIST",
    listType,
    style,
    quests,
    title,
    canEdit
    // useClone,
  } = props;

  return (
    <Droppable
      droppableId={listId}
      type={listType}
      ignoreContainerClipping={ignoreContainerClipping}
      isDropDisabled={isDropDisabled}
      isCombineEnabled={isCombineEnabled}
    >
      {(dropProvided: DroppableProvided, dropSnapshot: DroppableStateSnapshot) => (
        <Wrapper
          style={style}
          hasQuests={Boolean(quests && quests.length > 0)}
          isDraggingOver={dropSnapshot.isDraggingOver}
          isDropDisabled={Boolean(isDropDisabled)}
          isDraggingFrom={Boolean(dropSnapshot.draggingFromThisWith)}
          isDraggingAQuest={Boolean(props.isDraggingAQuest)}
          {...dropProvided.droppableProps}
        >
          <InnerList
            isDraggingAQuest={props.isDraggingAQuest}
            quests={quests}
            title={title}
            dropProvided={dropProvided}
            draggingOverWith={dropSnapshot.draggingOverWith}
            canEdit={canEdit}
          />
        </Wrapper>
      )}
    </Droppable>
  );
}
